import { useEffect, useLayoutEffect, useState } from "react";
import {
	Document,
	Page,
	Text,
	View,
	StyleSheet,
	PDFViewer,
	Image,
	Font,
	Svg,
	Path,
	G,
	Defs,
	ClipPath,
	PDFDownloadLink,
} from "@react-pdf/renderer";
import { PDI } from "../data/pdi";
import { useAppContext } from "../../../contexts/app";
import { useCategoryNav } from "../../../components/globals";
import { useParams } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import { useGoogleStorageImage } from "../../../hooks/useGoogleStorageImage";

// ----------------------- Fonts -----------------------
// Register Work Sans from Google (weights used: 300, 400, 700)
Font.register({
	family: "Work Sans",
	fonts: [
		{
			src: "https://fonts.gstatic.com/s/worksans/v18/QGY_z_wNahGAdqQ43Rh_fKDp.woff2",
			fontWeight: 300,
		},
		{
			src: "https://fonts.gstatic.com/s/worksans/v18/QGYsz_wNahGAdqQ43Rh_cqDpp_k.woff2",
			fontWeight: 400,
		},
		{
			src: "https://fonts.gstatic.com/s/worksans/v18/QGYsz_wNahGAdqQ43Rh_c6Dpp_k.woff2",
			fontWeight: 700,
		},
	],
});

// ----------------------- Styles -----------------------
const styles = StyleSheet.create({
	page: {
		paddingTop: 28,
		paddingBottom: 48, // leave room for footer
		paddingHorizontal: 28,
		fontSize: 10,
		fontFamily: "Work Sans",
	},

	// Titles: uppercase, thin (300), ~0.05em tracking
	title: {
		fontSize: 16,
		fontWeight: 300,
		marginBottom: 6,
		textTransform: "uppercase",
		letterSpacing: 0.8, // ~0.05em @ 16pt
	},
	subtitle: {
		fontSize: 10,
		color: "#666",
		marginBottom: 12,
		fontWeight: 400,
	},

	// Category headers: uppercase, bold (700), 0px letter spacing
	sectionHeader: {
		backgroundColor: "#111",
		color: "#fff",
		paddingVertical: 6,
		paddingHorizontal: 8,
		fontSize: 11,
		fontWeight: 700,
		marginTop: 14,
		marginBottom: 6,
		borderRadius: 2,
		textTransform: "uppercase",
		letterSpacing: 0,
	},

	subHeader: {
		fontSize: 10,
		fontWeight: 700,
		marginTop: 8,
		marginBottom: 4,
	},

	table: {
		width: "100%",
		borderStyle: "solid",
		borderWidth: 1,
		borderColor: "#ddd",
		borderRightWidth: 0,
		borderBottomWidth: 0,
		marginBottom: 10,
	},
	headerRow: {
		flexDirection: "row",
		backgroundColor: "#f6f6f6",
	},
	headerCell1: {
		textAlign: "left",
		borderStyle: "solid",
		borderRightWidth: 1,
		borderBottomWidth: 1,
		borderColor: "#ddd",
		padding: 6,
		fontSize: 9,
		fontWeight: 700,
		color: "#333",
	},
	headerCell: {
		textAlign: "center",
		borderStyle: "solid",
		borderRightWidth: 1,
		borderBottomWidth: 1,
		borderColor: "#ddd",
		padding: 6,
		fontSize: 9,
		fontWeight: 700,
		color: "#333",
	},
	row: { flexDirection: "row" },

	// Columns: Image | Label/Desc | Result | Date | By
	colImg: {
		width: 70,
		borderStyle: "solid",
		borderRightWidth: 1,
		borderBottomWidth: 1,
		borderColor: "#ddd",
		padding: 4,
		alignItems: "center",
		justifyContent: "center",
	},
	colLabel: {
		flex: 42,
		borderStyle: "solid",
		borderRightWidth: 1,
		borderBottomWidth: 1,
		borderColor: "#ddd",
		padding: 6,
	},
	colValue: {
		flex: 28,
		borderStyle: "solid",
		borderRightWidth: 1,
		borderBottomWidth: 1,
		borderColor: "#ddd",
		padding: 6,
		justifyContent: "center",
	},
	colOn: {
		flex: 10,
		textAlign: "center",
		borderStyle: "solid",
		borderRightWidth: 1,
		borderBottomWidth: 1,
		borderColor: "#ddd",
		padding: 6,
		justifyContent: "center",
	},
	colBy: {
		flex: 14,
		textAlign: "right",
		borderStyle: "solid",
		borderRightWidth: 1,
		borderBottomWidth: 1,
		borderColor: "#ddd",
		padding: 6,
		justifyContent: "center",
	},

	imgThumb: {
		width: "100%",
		// height: 42,
		objectFit: "contain",
	},

	label: {
		fontSize: 9,
		color: "#111",
		fontWeight: 700,
		marginBottom: 2,
		lineHeight: 1.3,
	},
	description: {
		fontSize: 8,
		color: "#555",
		lineHeight: 1.35,
	},
	value: {
		fontSize: 9,
		color: "#111",
		lineHeight: 1.35,
	},
	valueSmall: {
		marginTop: 2,
		fontSize: 8,
		lineHeight: 1.25,
		color: "#444",
	},
	mono: { fontFamily: "Courier" },
	metaText: {
		fontSize: 8,
		color: "#111",
	},

	// Pass/Fail styling
	passText: {
		color: "#0a7b33",
		fontWeight: 700,
	},
	failCell: {
		backgroundColor: "#d93025",
	},
	failText: {
		color: "#fff",
		fontWeight: 700,
	},
	failSmall: {
		color: "#fff",
	},

	// Not Completed styling
	ncCell: {
		backgroundColor: "#000",
	},
	ncText: {
		color: "#fff",
		fontWeight: 700,
	},
	ncSmall: {
		color: "#fff",
	},

	formHeader: {
		fontSize: 12,
		fontWeight: 700,
		marginTop: 4,
		marginBottom: 6,
		textTransform: "uppercase",
		letterSpacing: 0.6, // light tracking for small title
	},

	// Page-1 logo block
	logoContainer: {
		width: "100%",
		marginRight: "auto",
		marginLeft: "auto",
		paddingBottom: 10,
		flexDirection: "row",
		justifyContent: "center",
		alignItems: "center",
	},
	companyInfo: {
		fontSize: 8,
		color: "#555",
		marginLeft: 8,
	},

	// Footer
	footerWrap: {
		position: "absolute",
		left: 28,
		right: 28,
		bottom: 14,
		borderTopWidth: 1,
		borderTopColor: "#e5e5e5",
		paddingTop: 6,
		flexDirection: "column",
	},
	footerRow: {
		flexDirection: "row",
		alignItems: "center",
	},
	footerText: {
		fontSize: 8,
		color: "#555",
	},
	footerSep: {
		fontSize: 8,
		color: "#999",
		marginHorizontal: 4,
	},
	footerConfidential: {
		marginTop: 4,
		fontSize: 7.5,
		color: "#666",
		lineHeight: 1.25,
	},

	// Final sign off
	signoffWrap: { marginTop: 14 },
	signoffBox: {
		borderWidth: 1,
		borderColor: "#ccc",
		padding: 10,
		marginTop: 6,
	},
	signoffRow: {
		flexDirection: "row",
		alignItems: "center",
		marginTop: 8,
	},
	signoffLabel: { width: 88, fontSize: 9, color: "#555" },
	signoffLine: { borderBottomWidth: 1, borderColor: "#aaa", height: 16, flex: 1 },
	signoffValue: { fontSize: 10, color: "#111" },

	// Images gallery section
	imagesSection: {
		marginTop: 14,
	},
	imagesGrid: {
		flexDirection: "row",
		flexWrap: "wrap",
		gap: 10,
	},
	imageCard: {
		width: "48%",
		marginBottom: 10,
		borderWidth: 1,
		borderColor: "#ddd",
		borderRadius: 4,
		overflow: "hidden",
	},
	imageCardFull: {
		width: "100%",
		marginBottom: 10,
		borderWidth: 1,
		borderColor: "#ddd",
		borderRadius: 4,
		overflow: "hidden",
	},
	imageCardImage: {
		width: "100%",
		height: 180,
		objectFit: "contain",
		backgroundColor: "#f9f9f9",
	},
	imageCardLabel: {
		padding: 6,
		backgroundColor: "#f6f6f6",
		fontSize: 9,
		fontWeight: 700,
		color: "#333",
		textAlign: "center",
	},
});

// ----------------------- Branding Components -----------------------
const FlexSecurityLogo = ({ width = 149, height = 26 }) => (
	<Svg xmlns="http://www.w3.org/2000/svg" width="149" height="26" fill="none" style={{ width: 149, height: 26 }}>
		<Path fill="#000" fill-rule="evenodd" d="M26.974 22.767c.068 0 .135.017.2.053a.373.373 0 0 1 .152.15.415.415 0 0 1 0 .407.413.413 0 0 1-.556.15.412.412 0 0 1-.15-.558.376.376 0 0 1 .153-.15.417.417 0 0 1 .2-.052Zm0 .067a.347.347 0 0 0-.294.17.343.343 0 0 0-.001.339.343.343 0 0 0 .464.126.343.343 0 0 0 .125-.465.313.313 0 0 0-.128-.126.348.348 0 0 0-.166-.044Zm-.179.565v-.438h.15c.052 0 .09.004.112.012a.109.109 0 0 1 .055.043.116.116 0 0 1-.014.148.135.135 0 0 1-.09.04.12.12 0 0 1 .037.023c.017.017.04.046.065.087l.053.085h-.086l-.04-.069a.352.352 0 0 0-.073-.102.09.09 0 0 0-.057-.015h-.041v.186h-.071Zm.07-.246h.087c.04 0 .068-.006.083-.019a.06.06 0 0 0 .023-.048.059.059 0 0 0-.011-.035.065.065 0 0 0-.03-.023.224.224 0 0 0-.07-.008h-.081v.133ZM14.479 22.454c.666.162 1.36.254 2.072.27v2.942a12.68 12.68 0 0 1-2.834-.37l.762-2.842Zm4.597 0 .761 2.841c-.91.226-1.858.353-2.834.37v-2.94a9.731 9.731 0 0 0 2.073-.272Zm-6.966-.917a9.658 9.658 0 0 0 1.931.8l-.76 2.842a12.592 12.592 0 0 1-2.641-1.094l1.47-2.548Zm9.333-.001 1.471 2.547c-.828.46-1.712.83-2.64 1.095l-.761-2.841a9.67 9.67 0 0 0 1.93-.801Zm-11.382-1.498c.504.481 1.06.908 1.658 1.273l-1.471 2.548c-.82-.495-1.581-1.08-2.267-1.741l2.08-2.08Zm13.43-.002 2.08 2.08a12.717 12.717 0 0 1-2.265 1.74l-1.471-2.547a9.762 9.762 0 0 0 1.656-1.273ZM16.775 3.952a9.048 9.048 0 1 1 0 18.096 9.048 9.048 0 0 1 0-18.096ZM8.467 18.06a9.766 9.766 0 0 0 1.274 1.657l-2.08 2.08a12.716 12.716 0 0 1-1.742-2.266l2.548-1.471Zm16.618-.002 2.547 1.471c-.494.82-1.08 1.58-1.74 2.266l-2.08-2.08a9.762 9.762 0 0 0 1.273-1.657Zm-2.175-3.476H10.64a6.336 6.336 0 0 0 12.269 0ZM7.44 15.739c.197.678.467 1.324.8 1.931L5.693 19.14c-.46-.827-.83-1.712-1.096-2.64l2.842-.762Zm18.672 0 2.842.76a12.594 12.594 0 0 1-1.095 2.64l-2.548-1.47a9.67 9.67 0 0 0 .8-1.93ZM4.11 13.227H7.05c.016.713.11 1.406.271 2.073l-2.842.762a12.682 12.682 0 0 1-.37-2.835Zm25.331 0a12.68 12.68 0 0 1-.37 2.834l-2.842-.762c.161-.666.254-1.36.27-2.072h2.942Zm-13.535-1.133a.433.433 0 0 0-.064.005l-.037.007a1.313 1.313 0 0 1-.188.02h-.034l-.01-.001-.021-.002h-.012l-.026-.003-.028-.004-.03-.004-.046-.006-.04-.005-.023-.003-.022-.002-.02-.001h-.025c-.063 0-.095.03-.095.09s.032.092.098.092h.01l.006-.001.014-.002.007-.001.036-.006h.008l.013-.003a.17.17 0 0 1 .016 0c.067 0 .1.033.1.1 0 .01 0 .022-.002.036l-.002.01-.004.02-.002.01-.005.023-.006.025-.008.027-.008.029-.336 1.106-.354-1.126-.028-.094a.277.277 0 0 1-.013-.08c0-.046.034-.071.101-.073H14.926c.064 0 .096-.032.096-.096 0-.058-.031-.087-.093-.087h-.027l-.01.001-.021.002-.024.002-.027.003-.028.003-.03.004-.033.005-.03.003-.029.004-.026.003-.024.002-.022.001-.02.001h-.018c-.066 0-.14-.007-.224-.022l-.04-.007a.325.325 0 0 0-.053-.005c-.067 0-.1.031-.1.093 0 .057.03.087.09.09H14.335c.05 0 .087.015.11.045.005.005.01.014.015.025l.007.015.007.017.008.02.008.022.008.025.45 1.328c.028.087.083.13.163.13.074 0 .125-.04.155-.12l.003-.01.412-1.272.014-.044.01-.026.008-.024.004-.01.01-.026.01-.022.006-.015.006-.012a.152.152 0 0 1 .006-.01c.016-.027.037-.042.062-.044h.02l.057.005.007.002h.004c.061 0 .092-.03.092-.09s-.03-.092-.092-.092Zm3.07 0h-.013c-.05 0-.087.034-.11.1l-.003.011-.424 1.426-.386-1.338-.018-.07c-.021-.082-.062-.125-.123-.129H17.878l-.026.002-.02.002-.034.003-.042.006-.03.003-.014.002-.026.002a.657.657 0 0 1-.14-.004l-.026-.003-.088-.01-.014-.002H17.4c-.075 0-.113.03-.113.092 0 .056.026.085.078.085l.011-.001.014-.001.017-.002.037-.006.016-.002.014-.001h.012c.082 0 .125.061.128.185V13.521c0 .132-.043.199-.128.199a.15.15 0 0 1-.016-.002h-.007l-.015-.003-.03-.006a.295.295 0 0 0-.053-.005.071.071 0 0 0-.055.025.09.09 0 0 0-.023.062c0 .06.036.09.106.092h.025a.103.103 0 0 1 .012-.002h.003l.11-.022.01-.002h.013l.023-.002.019-.001.031-.001h.024l.054-.001c.051 0 .094.002.128.008l.049.008.046.007.041.006.016.002h.018c.067 0 .1-.03.1-.09 0-.057-.03-.087-.089-.09H17.911c-.063 0-.096-.06-.1-.179v-.012l-.015-1.224c0-.01.003-.015.01-.015.006 0 .01.003.015.011l.003.004.457 1.518c.011.035.027.059.046.071.02.013.053.02.097.02.07 0 .116-.03.137-.092l.003-.008.462-1.509c.005-.01.01-.015.015-.015.007 0 .011.003.012.011v.709l-.001.079-.002.084-.001.055v.175l-.002.051-.001.047-.001.021v.008l-.003.016c-.016.12-.05.18-.106.185h-.011l-.017-.002-.044-.005a.155.155 0 0 0-.038-.003c-.067 0-.101.03-.101.091 0 .06.032.089.098.089.02 0 .041-.002.065-.006l.015-.002.027-.005c.064-.01.13-.014.2-.014.073 0 .162.006.267.018l.026.003.034.005h.012c.066-.002.1-.034.1-.095 0-.053-.025-.081-.073-.084h-.024l-.05.005a.217.217 0 0 1-.054.008c-.081 0-.123-.062-.126-.186v-1.073c0-.133.043-.199.128-.199h.011l.014.002.016.002.036.006.016.002.014.001h.011c.054 0 .08-.028.08-.084 0-.06-.035-.09-.106-.093H19.441a.1.1 0 0 0-.016.002h-.002l-.088.017a.623.623 0 0 1-.102.007h-.06l-.025-.001a.558.558 0 0 1-.041-.004h-.011l-.013-.003-.025-.005-.017-.005a.405.405 0 0 0-.058-.008h-.007Zm-6.917 0c-.046 0-.095.002-.149.004l-.027.002-.217.011a3.161 3.161 0 0 1-.124.004h-.085l-.027-.002a.768.768 0 0 1-.025-.002l-.146-.015-.017-.001h-.011c-.074 0-.11.03-.11.092 0 .023.007.043.022.06a.072.072 0 0 0 .055.025l.011-.001h.007l.015-.002.008-.001.038-.006.016-.002.014-.001h.011c.083 0 .126.061.128.185v1.073c0 .133-.042.199-.127.199a.183.183 0 0 1-.042-.005l-.011-.003-.039-.003-.02-.002h-.009c-.052 0-.078.028-.078.084 0 .061.033.093.1.095h.008l.158-.02a1.01 1.01 0 0 1 .295-.005l.02.002.135.018.037.006c.072 0 .108-.031.108-.093 0-.055-.024-.084-.073-.087h-.035l-.017.002-.02.003a.18.18 0 0 1-.019.002h-.034c-.066 0-.107-.04-.124-.121l-.002-.013-.001-.01-.001-.018-.001-.034v-.036l-.001-.055v-.27c0-.066.024-.1.072-.104H11.965c.133 0 .221.02.264.057.04.036.062.11.064.22V13.568c0 .21.098.315.296.315a.298.298 0 0 0 .227-.086.325.325 0 0 0 .081-.224v-.033l.001-.007v-.007l.002-.012v-.198l.003-.056v-.01c-.003-.069-.033-.103-.09-.103-.04 0-.065.015-.079.045a.258.258 0 0 0-.009.068v.217c0 .15-.04.226-.117.226-.074 0-.112-.06-.114-.178v-.165c0-.134-.02-.23-.056-.288-.038-.058-.105-.096-.203-.116a.826.826 0 0 0 .186-.063c.127-.063.19-.182.19-.356 0-.295-.184-.443-.552-.443Zm9.743 0H21.765l-.027.001-.029.002-.047.002-.035.003-.036.002-.039.003-.082.007-.035.003-.032.002h-.016l-.019.001h-.029c-.053 0-.099-.002-.136-.006l-.014-.001-.123-.016a.297.297 0 0 0-.038-.003c-.073 0-.11.031-.11.093 0 .058.027.087.08.087h.01l.013-.001.025-.003.03-.004h.01l.017-.002h.015c.081 0 .124.06.126.182V13.52c0 .132-.042.198-.128.198a.197.197 0 0 1-.052-.005.352.352 0 0 0-.068-.008.071.071 0 0 0-.055.025.09.09 0 0 0-.023.062c0 .06.035.09.104.092H21.05a.083.083 0 0 1 .012-.002h.003l.145-.021a.966.966 0 0 1 .124-.008h.065l.028.001h.013l.029.002h.005l.02.002.01.002.133.02.035.005c.074 0 .11-.032.11-.096 0-.053-.023-.081-.07-.084H21.704l-.021.001-.045.004a.173.173 0 0 1-.053.008c-.06 0-.096-.024-.109-.071v-.005l-.003-.01-.002-.012-.002-.021-.003-.026-.001-.02-.003-.043-.002-.052v-.028l-.002-.046v-.195c0-.036.003-.058.01-.068.008-.008.022-.013.044-.014H21.8c.205 0 .36-.044.469-.133.108-.088.162-.215.162-.38a.502.502 0 0 0-.094-.307.427.427 0 0 0-.26-.164 1.251 1.251 0 0 0-.276-.027Zm.009.17c.27 0 .406.111.406.335 0 .17-.074.273-.223.31a1.212 1.212 0 0 1-.273.029H21.552c-.06-.007-.092-.032-.095-.075V12.417l.002-.025v-.005l.002-.017a.128.128 0 0 1 .013-.05c.011-.018.043-.031.095-.04l.012-.002.02-.003.011-.002h.011l.024-.003.026-.002.035-.002h.037l.032-.001h.034Zm-9.798-.014c.262 0 .393.1.393.302 0 .12-.043.205-.129.256a.601.601 0 0 1-.296.064h-.256c-.028 0-.047-.004-.058-.012-.009-.007-.014-.023-.014-.048v-.398c.001-.065.027-.109.076-.13a.266.266 0 0 1 .052-.017l.013-.002.02-.004a.87.87 0 0 1 .045-.005l.016-.001.027-.002.03-.001.03-.001H12.013ZM4.48 9.941l2.841.761a9.737 9.737 0 0 0-.27 2.074H4.11c.017-.976.144-1.925.37-2.835Zm24.591 0c.226.91.353 1.86.37 2.835H26.5a9.733 9.733 0 0 0-.27-2.073l2.841-.761ZM16.776 6.667a6.336 6.336 0 0 0-6.134 4.75H22.91a6.336 6.336 0 0 0-6.135-4.75Zm11.086.198c.459.828.828 1.713 1.094 2.64l-2.842.762a9.665 9.665 0 0 0-.8-1.93l2.548-1.472ZM5.69 6.863l2.548 1.47a9.667 9.667 0 0 0-.8 1.932l-2.843-.761a12.59 12.59 0 0 1 1.095-2.641Zm20.202-2.657a12.721 12.721 0 0 1 1.741 2.267l-2.548 1.47a9.767 9.767 0 0 0-1.273-1.657l2.08-2.08ZM7.66 4.204l2.08 2.08a9.766 9.766 0 0 0-1.274 1.658L5.918 6.47c.494-.821 1.08-1.581 1.741-2.267Zm15.648-2.06c.82.495 1.58 1.08 2.266 1.742l-2.08 2.08a9.766 9.766 0 0 0-1.657-1.274l1.471-2.548Zm-13.06-.002 1.47 2.548a9.766 9.766 0 0 0-1.658 1.274l-2.08-2.08a12.719 12.719 0 0 1 2.267-1.742ZM20.274.822c.928.266 1.813.636 2.64 1.095l-1.47 2.548a9.668 9.668 0 0 0-1.931-.801l.761-2.842ZM13.28.82l.762 2.842a9.665 9.665 0 0 0-1.932.8l-1.471-2.547A12.59 12.59 0 0 1 13.279.82Zm3.724-.487c.976.018 1.925.145 2.835.371l-.761 2.842a9.737 9.737 0 0 0-2.073-.271V.334Zm-.452 0v2.942a9.737 9.737 0 0 0-2.073.27L13.716.704c.91-.226 1.86-.353 2.835-.37Z" clip-rule="evenodd" />
		<G fill="#000" clip-path="url(#a)">
			<Path d="M146.139 6.673V5.327h.683c.055 0 .114.016.179.047.064.03.119.078.164.143a.4.4 0 0 1 .069.24.431.431 0 0 1-.071.252.475.475 0 0 1-.363.208h-.493v-.219h.402c.04 0 .083-.02.129-.06.048-.04.071-.1.071-.18 0-.083-.023-.14-.071-.168a.237.237 0 0 0-.122-.044h-.281v1.127h-.296Zm.814-.635.336.635h-.325l-.328-.635h.317Zm-.303 1.438c-.202 0-.391-.037-.567-.113a1.466 1.466 0 0 1-.893-1.346 1.472 1.472 0 0 1 .893-1.347c.176-.076.365-.113.567-.113a1.432 1.432 0 0 1 1.033.427 1.458 1.458 0 0 1-.465 2.38 1.433 1.433 0 0 1-.568.112Zm0-.35c.205 0 .391-.05.559-.15.168-.1.301-.233.401-.401.1-.168.15-.354.15-.558 0-.205-.05-.391-.15-.559a1.133 1.133 0 0 0-.401-.401c-.168-.1-.354-.15-.559-.15-.204 0-.39.05-.558.15-.168.1-.302.233-.402.401a1.08 1.08 0 0 0-.149.559c0 .204.05.39.149.558.1.168.234.302.402.401.168.1.354.15.558.15Z" />
			<Path fill-rule="evenodd" d="M33.885 21.222V4.778h110.796v16.444H33.885ZM71.21 5.463H39.07v15.073h32.14V5.463ZM36.806 17.555h-.031c-.122.01-.198.077-.198.19 0 .076.036.137.105.167l.018.005a.497.497 0 0 0 .115.013h.272c.135.002.181.027.181.06 0 .035-.035.055-.152.055h-.213c-.17.002-.295.027-.38.08l-.027.018a.342.342 0 0 0-.052.05l-.01.011-.013-.028c-.094-.18-.271-.272-.518-.272-.419 0-.635.26-.635.742l.001.103.005.112.017.291.003.114v.082l-.003.047-.022.222-.001.03c.004.126.076.204.2.204.049 0 .093-.016.13-.048a.163.163 0 0 0 .059-.126l-.002-.029-.01-.078-.004-.031v-.012c.002-.044.043-.066.162-.066h1.361c.09.006.12.03.12.072l-.001.019-.007.029-.008.093v.011c.005.105.078.166.19.166.128 0 .203-.081.203-.21l-.001-.01-.027-.19-.008-.074a1.136 1.136 0 0 1 .005-.28l.03-.207v-.024c-.004-.121-.077-.198-.199-.198-.117 0-.193.067-.193.177l.002.038.006.059.002.035v.016c-.003.034-.026.052-.094.064h-.01l-.06.003-.11.001h-.344l-.027-.003-.008-.002V18.709l.002-.032c.006-.094.023-.155.046-.18.028-.03.104-.05.229-.05h.3c.295-.005.459-.158.459-.435a.429.429 0 0 0-.134-.331.485.485 0 0 0-.302-.118l-.087-.003-.026-.002h-.24l-.066-.003Zm-.885.757c.121 0 .2.038.247.114.044.07.067.182.067.337v.289h-.495c-.041-.004-.063-.017-.076-.044a.206.206 0 0 1-.011-.035l-.01-.047-.007-.066-.005-.1v-.076c.004-.26.097-.372.29-.372Zm58.618-9.08c-.992 0-1.774.355-2.345 1.067-.571.712-.857 1.686-.857 2.923 0 1.2.318 2.145.953 2.838.635.693 1.501 1.04 2.599 1.04.894 0 1.625-.226 2.193-.677.567-.45.851-1.03.851-1.737 0-.358-.092-.641-.276-.85-.184-.208-.434-.313-.75-.313a.878.878 0 0 0-.609.213.682.682 0 0 0-.225.558l.011.403a.678.678 0 0 1-.304.598c-.21.152-.481.229-.812.229-.534 0-.923-.188-1.167-.565-.244-.376-.367-.974-.367-1.793 0-1.58.504-2.37 1.511-2.37.324 0 .59.08.8.24.211.16.313.364.305.61l-.011.346c-.015.47.263.704.834.704.27 0 .49-.07.66-.212a.693.693 0 0 0 .254-.559c0-.07-.007-.162-.02-.279l-.03-.23a2.986 2.986 0 0 1-.03-.351l.003-.091c.004-.101.016-.233.034-.396l.02-.173c.023-.186.034-.328.034-.425a.684.684 0 0 0-.198-.508.697.697 0 0 0-.512-.196c-.451 0-.677.209-.677.626 0 .052.004.09.011.112-.533-.522-1.161-.782-1.883-.782Zm12.339.156c-.147 0-.359.015-.637.044l-.107.012a7.295 7.295 0 0 1-.744.056c-.127 0-.312-.015-.555-.044l-.094-.012a5.576 5.576 0 0 0-.648-.056.713.713 0 0 0-.547.224c-.139.149-.208.346-.208.592 0 .454.154.682.462.682h.113a.692.692 0 0 1 .135-.011c.105 0 .173.035.203.106.03.07.045.244.045.52v2.559c0 .499-.096.858-.288 1.078-.191.22-.498.33-.919.33-.42 0-.719-.106-.896-.319-.177-.212-.265-.575-.265-1.09v-2.95c.023-.156.098-.234.226-.234.06 0 .109.003.146.01h.113c.308 0 .462-.223.462-.67 0-.253-.067-.454-.203-.603a.694.694 0 0 0-.541-.224c-.127 0-.309.015-.546.044l-.091.012a5.312 5.312 0 0 1-.637.056c-.147 0-.359-.015-.637-.044l-.107-.012a7.293 7.293 0 0 0-.744-.056c-.263 0-.476.075-.637.224-.162.149-.243.35-.243.603 0 .209.058.382.175.52a.546.546 0 0 0 .434.207c.054 0 .123-.009.206-.025l.065-.014c.112-.027.195-.04.248-.04.218 0 .327.202.327.604v.522l-.002.208c-.002.184-.007.394-.013.63l-.042 1.534c0 .775.158 1.36.474 1.755.519.648 1.398.973 2.638.973 1.203 0 2.101-.313 2.695-.94.288-.298.467-.705.539-1.218l.013-.111c.022-.157.033-.62.034-1.39v-.454l-.011-.984V11.5c0-.424.109-.637.327-.637a.8.8 0 0 1 .143.018l.099.021c.109.027.194.04.254.04.188 0 .34-.067.457-.201a.774.774 0 0 0 .174-.526c0-.253-.077-.454-.231-.603-.154-.15-.359-.224-.614-.224Zm-27.79-.156c-.736 0-1.335.214-1.798.642-.462.429-.693.986-.693 1.671 0 .678.27 1.218.812 1.62.278.21.578.355.902.437l.744.178c.624.15 1.018.278 1.184.386.165.108.248.292.248.553a.707.707 0 0 1-.282.581c-.188.15-.44.224-.755.224-.746 0-1.17-.26-1.275-.777l-.01-.061c-.053-.336-.142-.565-.266-.688-.124-.123-.329-.184-.614-.184-.256 0-.464.061-.626.184a.571.571 0 0 0-.242.475c0 .047.003.108.009.185l.033.331c.01.104.014.186.014.244 0 .127-.015.31-.045.548a4.04 4.04 0 0 0-.034.503c0 .477.244.715.733.715.428 0 .654-.209.676-.626.617.477 1.26.715 1.928.715.805 0 1.466-.24 1.985-.72.518-.481.778-1.097.778-1.85 0-.64-.215-1.147-.643-1.52-.406-.353-1.044-.622-1.916-.808l-.349-.071c-.447-.095-.73-.178-.852-.25-.14-.082-.209-.238-.209-.47 0-.446.323-.67.97-.67.684 0 1.082.242 1.195.727l.056.234c.083.358.338.537.767.537.511 0 .766-.22.766-.66v-.055c0-.056-.005-.133-.015-.23l-.007-.061a2.674 2.674 0 0 1-.023-.29c0-.084.007-.197.02-.34l.022-.23c.017-.179.026-.316.026-.414 0-.402-.24-.604-.722-.604-.233 0-.396.049-.49.146-.094.097-.145.275-.152.536-.451-.529-1.067-.793-1.85-.793Zm10.765.156c-.283 0-.556.007-.82.022l-.327.02c-.103.006-.236.011-.398.017l-2.02.053h-.02c-.55 0-1.117-.022-1.703-.067l-.25-.018a9.033 9.033 0 0 0-.528-.027c-.549 0-.823.272-.823.816 0 .209.06.384.18.525.12.142.267.213.44.213.056 0 .119-.007.188-.02l.176-.036c.078-.015.134-.023.166-.023.203 0 .304.25.304.75v3.095c0 .507-.101.76-.304.76-.044 0-.112-.01-.202-.027l-.058-.012c-.12-.026-.21-.04-.27-.04-.18 0-.33.072-.446.213a.8.8 0 0 0-.174.525c0 .239.073.436.22.593a.745.745 0 0 0 .569.234h.056l.035-.01.867-.08c.248-.021.56-.033.936-.033h1.004l.91.015c.699.014 1.245.034 1.64.059l.264.017c.234.014.409.021.523.021.443 0 .665-.175.665-.525 0-.15-.011-.294-.034-.436l-.022-.147a2.736 2.736 0 0 1-.034-.334l.003-.083c.007-.116.024-.312.053-.587.023-.216.034-.421.034-.615 0-.596-.274-.894-.823-.894-.27 0-.485.078-.643.235-.157.156-.236.372-.236.648 0 .078.007.17.021.279l.013.084c.022.146.033.27.033.375a.438.438 0 0 1-.287.436c-.192.082-.528.123-1.01.123h-.653c-.256 0-.43-.047-.525-.14-.094-.093-.14-.27-.14-.531v-.838c0-.239.108-.358.326-.358.324 0 .485.09.485.268l-.017.173-.005.08v.071c0 .38.188.57.564.57.398 0 .597-.22.597-.659l-.002-.082a7.571 7.571 0 0 0-.027-.376l-.016-.168a7.603 7.603 0 0 1-.045-.738c0-.166.01-.345.029-.536l.016-.145c.015-.127.022-.242.022-.347 0-.38-.191-.57-.575-.57-.368 0-.552.157-.552.47 0 .067.004.123.011.167v.146c0 .238-.165.357-.496.357-.143 0-.23-.031-.265-.095a.625.625 0 0 1-.05-.285v-.626c0-.23.056-.38.169-.447.082-.044.248-.067.496-.067h1.116c.255 0 .413.008.474.023.172.037.259.137.259.301 0 .07-.013.174-.038.31l-.013.07c-.034.172-.05.306-.05.403 0 .573.281.86.845.86.24 0 .442-.074.603-.223a.716.716 0 0 0 .243-.548c0-.084-.01-.198-.029-.342l-.022-.155c-.034-.22-.05-.39-.05-.509l.002-.1c.004-.091.013-.205.027-.34l.019-.169.015-.152c.01-.118.015-.215.015-.29 0-.364-.21-.547-.631-.547Zm31.562 0-.069.001c-.091.004-.22.012-.389.026l-.552.05a8.649 8.649 0 0 1-.682.035c-.263 0-.533-.015-.811-.045l-.386-.04a5.175 5.175 0 0 0-.404-.027c-.248 0-.445.073-.592.218-.146.145-.219.345-.219.598 0 .209.058.384.174.525a.542.542 0 0 0 .434.213c.055 0 .124-.009.21-.025l.067-.014c.116-.027.205-.04.265-.04.15 0 .244.097.282.291.013.06.021.19.022.39v3.164c0 .238-.007.395-.022.47-.038.193-.132.29-.282.29-.05 0-.12-.01-.209-.027l-.056-.012a1.331 1.331 0 0 0-.265-.04.558.558 0 0 0-.446.208.796.796 0 0 0-.174.53c0 .246.075.444.225.593.15.149.346.223.586.223l.068-.001c.154-.006.42-.028.801-.066.368-.037.721-.056 1.059-.056.218 0 .418.011.598.034l.71.09a.733.733 0 0 0 .66-.224.83.83 0 0 0 .22-.593.797.797 0 0 0-.175-.525.54.54 0 0 0-.434-.212c-.05 0-.121.009-.213.027l-.115.024a1.188 1.188 0 0 1-.213.027c-.203 0-.305-.253-.305-.76v-3.096c0-.499.102-.749.305-.749.05 0 .121.01.213.028l.115.024c.092.018.163.027.213.027a.54.54 0 0 0 .434-.213.797.797 0 0 0 .175-.525c0-.544-.275-.816-.823-.816Zm-12.459-.011a.743.743 0 0 0-.569.235.83.83 0 0 0-.22.592c0 .209.058.384.175.525a.54.54 0 0 0 .434.213c.054 0 .124-.009.209-.025l.067-.014c.116-.027.205-.04.265-.04.138 0 .228.082.271.245l.011.046c.015.067.022.22.022.458v3.096c0 .507-.101.76-.304.76-.044 0-.111-.01-.202-.027l-.057-.012a1.436 1.436 0 0 0-.282-.04.54.54 0 0 0-.434.213.797.797 0 0 0-.175.525c0 .544.274.816.823.816.163 0 .354-.01.573-.031l.227-.025a7.22 7.22 0 0 1 .835-.056c.193 0 .417.014.671.041l.349.04c.207.02.378.031.513.031.526 0 .789-.272.789-.816a.835.835 0 0 0-.163-.525.49.49 0 0 0-.4-.212.99.99 0 0 0-.192.025l-.062.014c-.109.026-.19.039-.242.039-.188 0-.282-.253-.282-.76v-.257c0-.201.019-.34.056-.414.053-.111.184-.167.395-.167.518 0 .877.074 1.077.223.199.15.298.418.298.805v.078l-.022.648a.18.18 0 0 0-.012.079c0 .372.085.672.254.9.169.227.393.34.671.34.052 0 .117-.003.197-.009l.344-.033c.109-.009.195-.014.26-.014l.081.003c.047.002.102.006.165.011l.274.028c.105.01.187.014.246.014.241 0 .434-.073.581-.218.146-.145.22-.34.22-.587 0-.223-.051-.404-.152-.542a.475.475 0 0 0-.401-.206.96.96 0 0 0-.155.018l-.107.02a.96.96 0 0 1-.155.018c-.18 0-.27-.153-.27-.459v-.134c.007-.126.011-.257.011-.39 0-.448-.113-.785-.338-1.012-.226-.228-.609-.386-1.151-.475.467-.105.809-.231 1.027-.38.39-.261.586-.67.586-1.23 0-.61-.209-1.093-.626-1.447-.417-.354-.983-.53-1.697-.53l-.185.002c-.184.005-.42.017-.708.035l-.439.027a23.75 23.75 0 0 1-1.351.047 7.34 7.34 0 0 1-.722-.034l-.8-.078a.352.352 0 0 0-.102-.011Zm29.739.011a4.01 4.01 0 0 0-.496.044l-.084.012a5.13 5.13 0 0 1-.539.055h-.053c-.128 0-.376-.025-.744-.077a3.551 3.551 0 0 0-.497-.034c-.541 0-.811.265-.811.794 0 .232.082.402.246.508l.047.028c.15.082.225.164.225.246 0 .067-.064.216-.191.447l-.677 1.207-.609-1.207-.05-.096a1.287 1.287 0 0 1-.074-.161.352.352 0 0 1-.045-.179v-.033c0-.053.023-.09.068-.112l.13-.07c.029-.015.06-.033.095-.053.173-.09.26-.25.26-.48 0-.56-.256-.839-.767-.839-.11 0-.249.011-.42.033l-.087.012a5.82 5.82 0 0 1-.79.067c-.14 0-.334-.015-.581-.044l-.095-.012a5.725 5.725 0 0 0-.643-.056c-.526 0-.789.29-.789.872 0 .454.18.682.541.682.06 0 .137-.01.231-.029l.074-.016a1.08 1.08 0 0 1 .214-.034c.158 0 .285.082.383.246l1.669 2.716c.195.313.293.585.293.816v.313c0 .343-.109.514-.327.514-.054 0-.128-.007-.221-.02l-.123-.02a2.279 2.279 0 0 0-.344-.038c-.428 0-.642.25-.642.748 0 .239.075.433.225.582.15.149.35.223.598.223l.124-.002c.18-.005.414-.02.701-.044l.326-.028c.308-.025.548-.038.72-.038l.129.003c.14.004.313.015.521.033l.437.04a7.9 7.9 0 0 0 .638.036c.256 0 .459-.07.609-.212.151-.142.226-.336.226-.581a.837.837 0 0 0-.175-.548.552.552 0 0 0-.445-.212c-.065 0-.157.01-.277.028l-.062.01c-.15.027-.267.04-.349.04-.256 0-.376-.171-.361-.514l.011-.313c.015-.313.105-.604.271-.872l1.488-2.358c.166-.26.275-.417.327-.47.053-.052.132-.078.237-.078.042 0 .099.008.169.022l.056.012c.136.03.237.045.305.045a.503.503 0 0 0 .411-.207.812.812 0 0 0 .164-.52c0-.253-.073-.454-.22-.603a.779.779 0 0 0-.581-.224Zm-9.001 0c-.142 0-.319.008-.533.025l-.109.009c-.535.046-1.262.072-2.183.077h-.543l-.535-.003a28.432 28.432 0 0 1-1.58-.057l-.222-.017a7.986 7.986 0 0 0-.654-.034c-.331 0-.496.164-.496.492l.003.124c.003.091.01.199.02.323l.012.154c.014.19.021.322.021.394 0 .13-.007.271-.021.422l-.037.341a3.176 3.176 0 0 0-.021.298c0 .283.076.509.226.677.15.167.357.25.62.25.564 0 .846-.33.846-.994 0-.064-.01-.156-.029-.278l-.011-.063a2.249 2.249 0 0 1-.039-.34c0-.254.184-.38.552-.38.369 0 .553.25.553.748v3.219c0 .462-.128.693-.384.693-.059 0-.138-.007-.237-.02l-.129-.02a2.518 2.518 0 0 0-.366-.038c-.459 0-.688.246-.688.737 0 .544.282.816.845.816.188 0 .4-.01.635-.03l.143-.015a11.79 11.79 0 0 1 1.162-.067c.251 0 .541.014.87.041l.291.026c.301.03.56.045.778.045.564 0 .846-.294.846-.883 0-.447-.226-.67-.677-.67-.071 0-.171.01-.299.028l-.067.01c-.162.027-.284.04-.367.04-.255 0-.383-.231-.383-.693v-3.219c0-.275.045-.47.135-.58.09-.113.244-.169.462-.169.331 0 .497.135.497.403 0 .05-.005.116-.015.198l-.019.137c-.023.15-.034.268-.034.358 0 .64.282.96.846.96.255 0 .46-.081.614-.245.154-.164.231-.387.231-.67l-.004-.08-.056-.625a6.34 6.34 0 0 1-.018-.32l-.001-.048c0-.084.006-.198.016-.343l.025-.31c.01-.144.016-.258.016-.342 0-.328-.17-.492-.508-.492ZM56.428 10.8c.717 0 1.308.235 1.773.704.466.47.698 1.066.698 1.789l-.002.157c-.006.19-.024.312-.055.363-.038.063-.149.095-.332.095h-3.638c-.1 0-.164.013-.195.039-.03.026-.046.069-.046.128 0 .56.172 1.025.515 1.398.343.372.77.558 1.282.558.336 0 .643-.089.92-.268.28-.179.46-.406.544-.682l.103-.324c.054-.179.18-.268.378-.268.282 0 .423.142.423.425 0 .484-.251.914-.755 1.29a2.793 2.793 0 0 1-1.716.565c-.801 0-1.438-.262-1.911-.788-.473-.525-.71-1.231-.71-2.118 0-.91.252-1.647.756-2.213.503-.567 1.16-.85 1.968-.85Zm7.635.239.59.054a5.91 5.91 0 0 0 .962.004l.3-.029.144-.011.14-.007h.016c.328 0 .491.123.491.37 0 .24-.126.36-.38.36a.912.912 0 0 1-.192-.028l-.064-.016a1.105 1.105 0 0 0-.256-.043c-.179 0-.424.174-.736.523l-1.294 1.438 1.74 2.005a.48.48 0 0 0 .379.185.935.935 0 0 0 .106-.008l.2-.028.077-.007h.03c.267 0 .401.116.401.348 0 .254-.156.381-.468.381h-.09l-.434-.054-.1-.01a6.184 6.184 0 0 0-1.038.02l-.402.044h-.122c-.35 0-.525-.116-.525-.348 0-.255.138-.382.413-.382.097 0 .234.015.413.044.015.007.04.01.078.01.074 0 .111-.028.111-.086 0-.022-.01-.044-.033-.066l-.034-.043-1.238-1.417-1.26 1.417c-.052.058-.078.101-.078.13 0 .037.037.055.111.055a1.2 1.2 0 0 0 .122-.008l.225-.028.088-.007h.034c.32 0 .48.127.48.38 0 .233-.16.35-.48.35h-.156l-.446-.044a6.157 6.157 0 0 0-.592-.033c-.107 0-.23.007-.37.021l-.268.03-.139.012-.119.009a2.457 2.457 0 0 1-.14.004c-.313 0-.47-.123-.47-.37 0-.24.135-.36.402-.36l.065.003.085.008.147.016.075.005.03.001c.141 0 .342-.145.602-.436l1.55-1.732-1.55-1.787a.49.49 0 0 0-.38-.174.605.605 0 0 0-.133.01l-.19.044a.761.761 0 0 1-.156.011c-.245 0-.368-.116-.368-.349 0-.247.153-.37.457-.37h.048l.544.043.208.014.138.006.122.002.122-.002.117-.006.209-.016.266-.023.171-.011.108-.005.089-.002c.305 0 .457.123.457.37 0 .233-.141.349-.424.349a.622.622 0 0 1-.167-.022l-.167-.043H62.2c-.03 0-.045.007-.045.021a.39.39 0 0 0 .065.11l1.05 1.242 1.06-1.2c.067-.072.1-.122.1-.152-.007-.014-.025-.021-.055-.021a.675.675 0 0 0-.136.02l-.048.012a.864.864 0 0 1-.195.033c-.268 0-.402-.123-.402-.37 0-.24.156-.36.469-.36ZM46.684 8.889c.477 0 .86.094 1.147.28.287.187.431.438.431.753a.51.51 0 0 1-.159.39.56.56 0 0 1-.397.15.548.548 0 0 1-.38-.138.433.433 0 0 1-.154-.336c0-.102.038-.194.114-.274-.129-.118-.318-.176-.568-.176-.325 0-.58.1-.766.302-.186.202-.278.482-.278.841v.198c0 .095.049.143.147.143h.886c.25 0 .374.132.374.396 0 .263-.124.395-.374.395h-.84c-.129 0-.193.066-.193.198l.001 3.33.003.128.007.093c.038.198.133.297.284.297l.039-.004.06-.01.082-.017.103-.024.096-.012.075-.008.056-.002c.257 0 .386.12.386.363 0 .256-.155.384-.466.384-.096 0-.23-.008-.401-.024l-.367-.039-.158-.014-.134-.008-.11-.002c-.226 0-.43.01-.612.032l-.303.036-.161.014-.104.005c-.325 0-.488-.128-.488-.384 0-.096.04-.18.12-.253a.397.397 0 0 1 .277-.11c.057 0 .12.007.187.021l.069.017a.99.99 0 0 0 .255.039c.144 0 .235-.088.273-.264.038-.176.06-.601.068-1.275l.011-2.309c0-.132-.068-.198-.204-.198h-.568c-.265 0-.397-.135-.397-.406 0-.257.136-.385.409-.385h.647c.098 0 .147-.048.147-.143v-.198c0-.579.16-1.022.477-1.33.318-.308.768-.462 1.351-.462Zm4.477.239c.171 0 .257.104.257.314l-.002.078a.577.577 0 0 1-.01.084l-.017.206-.015.224-.013.244a36.614 36.614 0 0 0-.032 1.685l.01 3.365c.008.368.109.552.302.552.045 0 .138-.018.28-.054l.092-.014.065-.007h.021c.246 0 .369.122.369.367 0 .238-.153.357-.458.357l-.088-.002a6.617 6.617 0 0 1-.387-.03l-.279-.03-.162-.013-.14-.008a2.81 2.81 0 0 0-.117-.003l-.1.003-.123.008-.145.013-.334.038-.143.014-.12.008-.05.002h-.101c-.313 0-.47-.13-.47-.39 0-.223.127-.335.38-.335h.078l.06.008.041.003.202.03.09.011.032.002c.134 0 .218-.055.251-.167.034-.112.054-.402.062-.871l.01-.758.034-1.677-.011-1.926-.006-.149-.008-.122a1.404 1.404 0 0 0-.02-.162c-.052-.166-.156-.249-.312-.249l-.04.004-.05.007-.2.032a1.207 1.207 0 0 1-.202.022c-.253 0-.38-.115-.38-.346 0-.238.15-.357.447-.357.082 0 .153.003.213.01l.268.033c.119.014.22.022.301.022.062 0 .15-.01.264-.027l.071-.011a2.2 2.2 0 0 1 .335-.038Zm-15.694 4.72c-.123 0-.2.074-.2.193 0 .024.002.048.006.073l.019.114c.01.069.015.128.015.177l-.004.067-.007.062-.013.088-.01.08-.004.053-.001.034v.025c.01.106.082.171.196.171.124 0 .2-.076.2-.198l-.002-.027-.003-.025-.008-.049-.004-.029v-.016c.002-.02.007-.024.028-.025h.024l.033.004.06.012.096.025 1.113.326-1.14.345-.13.037-.02.004a.235.235 0 0 1-.04.004l-.004-.011-.003-.024v-.124l-.002-.026c-.01-.106-.085-.17-.201-.17-.12 0-.193.075-.193.193l.001.044.005.061.008.068.02.146.005.058.003.047v.064a1.541 1.541 0 0 1-.027.209l-.008.045-.004.028-.003.05c0 .124.074.202.2.202.122 0 .195-.077.195-.199v-.114l.002-.02c.002-.025.01-.04.022-.048l.018-.01.055-.022.036-.012 1.672-.545.032-.01c.12-.049.187-.14.187-.262 0-.13-.079-.222-.22-.266l-1.635-.51-.073-.026-.056-.021-.027-.013-.013-.007-.009-.007v-.015l.005-.056.004-.02v-.009c0-.118-.074-.192-.196-.192Zm20.972-2.311c-.427 0-.816.15-1.167.453-.35.301-.526.639-.526 1.011 0 .105.084.157.252.157h2.734c.16 0 .24-.052.24-.157 0-.395-.152-.737-.457-1.028a1.509 1.509 0 0 0-1.076-.436Zm56.001-.662c.692 0 1.037.253 1.037.76 0 .573-.488.86-1.465.86-.158 0-.265-.026-.322-.078-.056-.052-.084-.164-.084-.335v-.671c0-.246.082-.406.248-.48.09-.038.285-.056.586-.056Zm-74.982-1.3c-.116 0-.19.067-.19.177l.001.025.007.068.005.023a.15.15 0 0 1 .003.029c0 .045-.038.069-.162.069h-1.348c-.101-.005-.133-.028-.133-.072l.001-.023.01-.057.004-.036v-.026c0-.11-.073-.177-.189-.177-.128 0-.2.086-.2.22l.002.026.001.015.025.131.004.042.002.052v.034l-.002.061-.005.052-.003.017-.01.042-.006.03a.472.472 0 0 0-.007.048v.047l.003.026c.016.085.086.145.196.176l1.497.429-1.389.386-.083.02-.031.009c-.122.038-.193.115-.193.226l.002.053.007.067.011.087.004.045.002.045c0 .029-.001.059-.004.09l-.02.17-.002.029v.028c.01.118.08.191.2.191.115 0 .189-.065.189-.174l-.003-.039-.01-.068-.003-.03v-.019c.007-.04.049-.06.162-.06h1.353c.095.004.129.026.132.067l-.001.015-.004.022-.007.033-.004.028a.373.373 0 0 0-.004.051c0 .05.02.093.06.127a.2.2 0 0 0 .133.047c.128 0 .2-.085.2-.219v-.029l-.004-.011v-.004l-.028-.137-.005-.048-.002-.057-.001-.09v-.034a.79.79 0 0 1 .009-.106l.026-.165.003-.022.002-.027-.002-.029c-.01-.108-.08-.176-.195-.176-.123 0-.196.077-.196.199v.13c-.001.004-.003.006-.007.008l-.014.008-.018.006a.472.472 0 0 1-.117.013l-1.042.012 1.434-.416.03-.01a.214.214 0 0 0 .105-.08.285.285 0 0 0 .038-.158l-.003-.042a.237.237 0 0 0-.182-.204l-1.448-.427h.425l.2.004.287.002.12.003.025.001.055.007c.05.009.086.02.108.033.015.008.017.012.017.017l-.008.064-.002.012a.308.308 0 0 0-.003.048c0 .124.074.202.198.202.122 0 .194-.077.194-.198a.555.555 0 0 0-.004-.071l-.012-.07-.009-.073c-.005-.05-.007-.1-.007-.153v-.067c.004-.08.012-.17.025-.275l.005-.037.003-.01v-.02l-.003-.025c-.014-.108-.087-.173-.2-.173ZM35.97 6.149a.727.727 0 0 0-.432.128.59.59 0 0 0-.236.358 1.517 1.517 0 0 0-.035.348v.044l.003.069.006.1.02.247.004.093-.001.064-.005.073-.026.198-.001.036c0 .13.072.213.2.213.105 0 .177-.053.19-.144l.002-.04-.002-.026-.008-.07-.003-.034V7.79c.007-.04.048-.06.159-.06h1.33c.113.003.148.027.148.073v.026l-.001.008-.007.031a.45.45 0 0 0-.008.08c0 .048.02.092.06.126a.2.2 0 0 0 .133.047c.128 0 .2-.084.2-.216v-.032l-.004-.011-.029-.194-.004-.042-.003-.067V7.5l.001-.066.003-.042.003-.034.032-.214v-.019c-.004-.123-.078-.201-.202-.201-.116 0-.19.066-.19.174l.002.033.006.065.005.017.003.03c0 .037-.006.047-.025.052l-.025.004-.024.003-.051.004-.116.006-.091.002h-.259v-.357c-.004-.249-.064-.444-.183-.585-.127-.149-.309-.223-.54-.223Zm.01.418c.172 0 .266.065.302.205l.005.018c.02.084.03.19.03.317l-.001.174c-.003.018-.008.03-.012.034h-.508l-.035-.001-.06-.004-.021-.003-.005-.012a.367.367 0 0 1-.014-.06l-.008-.075-.004-.06-.002-.052-.001-.076.002-.056c.013-.243.12-.35.331-.35Z" clip-rule="evenodd" />
		</G>
		<Defs>
			<ClipPath id="a">
				<Path fill="#fff" d="M33.885 4.778H148.11v16.443H33.885z" />
			</ClipPath>
		</Defs>
	</Svg>
);

const Footer = ({ lastPage = false }) => (
	<View style={styles.footerWrap} fixed>
		<View style={styles.footerRow}>
			<FlexSecurityLogo width={70} height={12} />
			<Text style={[styles.footerText, { marginLeft: 8 }]}>© 2026</Text>
			<Text style={styles.footerSep}>•</Text>
			<Text style={styles.footerText}>www.flexsecurity.com</Text>
			<Text style={styles.footerSep}>•</Text>
			<Text style={styles.footerText}>(855) 427-7978</Text>
		</View>
		{lastPage ? (
			<Text style={styles.footerConfidential}>
				Please Note: All information contained in this communication is confidential and the property of RV Mobile Power LLC ("RVMP"). This message, including any attachments, may not be forwarded, printed, or shared without the direct approval of RVMP. Unauthorized use, disclosure, or distribution is strictly prohibited.
			</Text>
		) : null}
	</View>
);

// ----------------------- Helpers -----------------------
const decodeEntities = (s) => {
	if (!s) return "";
	const mapping = [
		[/&quot;/g, '"'],
		[/&#39;/g, "'"],
		[/&amp;/g, "&"],
		[/&lt;/g, "<"],
		[/&gt;/g, ">"],
		[/&nbsp;/g, " "],
		[/&bull;/g, "•"],
		[/&quote;/g, '"'],
	];
	let out = String(s);
	mapping.forEach(([re, repl]) => (out = out.replace(re, repl)));
	out = out.replace(/<[^>]*>/g, "");
	return out;
};

const joinDescription = (desc) => {
	if (!desc) return null;
	if (Array.isArray(desc)) return desc.filter(Boolean).map((d) => decodeEntities(d)).join("\n");
	return decodeEntities(desc);
};

// const normalizeAnswer = (v) => {
// 	if (v === undefined || v === null) return undefined;
// 	if (typeof v === "boolean") return v ? "Yes" : "No";
// 	if (Array.isArray(v)) return v.join(", ");

// 	const s = String(v).trim();
// 	if (s === "") return undefined;

// 	const lower = s.toLowerCase();

// 	// Map common truthy/falsy
// 	if (["yes", "true", "1"].includes(lower)) return "Yes";
// 	if (["no", "false", "0"].includes(lower)) return "No";

// 	// Map "NA" variants to "N/A"
// 	if (["n/a", "na", "n.a.", "n a", "not applicable"].includes(lower)) return "N/A";

// 	return s;
// };

const normalizeAnswer = (v) => {
	if (v === undefined || v === null) return undefined;
	if (typeof v === "boolean") return v ? "Yes" : "No";
	if (Array.isArray(v)) return v.join(", ");

	const s = String(v).trim();
	if (s === "") return undefined;

	const lower = s.toLowerCase();

	// Map common truthy/falsy
	if (["yes", "true", "1"].includes(lower)) return "Yes";
	if (["no", "false", "0"].includes(lower)) return "No";

	// Map "NA" variants to "N/A"
	if (["n/a", "na", "n.a.", "n a", "not applicable"].includes(lower)) return "N/A";

	return s;
};

// MM/DD/YYYY
const formatTimestamp = (ts) => {
	if (!ts) return undefined;
	try {
		const d = new Date(ts);
		if (isNaN(d.getTime())) return String(ts);
		const mm = String(d.getMonth() + 1).padStart(2, "0");
		const dd = String(d.getDate()).padStart(2, "0");
		const hours = d.getHours();
		const minutes = String(d.getMinutes()).padStart(2, '0');
		const ampm = hours >= 12 ? 'PM' : 'AM';
		const formattedHours = String(hours % 12 || 12).padStart(2, '0');
		return `${mm}/${dd}/${d.getFullYear()} (${formattedHours}:${minutes} ${ampm})`;

	} catch {
		return String(ts);
	}
};

const getValue = (key, formValues) => {
	if (!key) return "Not Completed";
	const raw = formValues?.[key];
	const normalized = normalizeAnswer(raw);
	return normalized ?? "Not Completed";
};

const getAnswerParts = (key, formValues) => {
	const base = getValue(key, formValues);
	if (base === "Not Completed") return { base, details: undefined };
	const det = normalizeAnswer(formValues?.[`${key}_Details`]);
	return { base, details: det };
};

const getMetaIfCompleted = (key, formValues) => {
	const base = getValue(key, formValues);
	if (base === "Not Completed") return { completedOn: "", completedBy: "" };
	const completedOn = formatTimestamp(formValues?.[`${key}_Timestamp`]) || "—";
	const completedBy = formValues?.[`${key}_User`] ? String(formValues[`${key}_User`]) : "—";
	return { completedOn, completedBy };
};

const collectAllFormQuestions = (PDIobj, version = 'v1') => {
	const questions = [];
	// PDI.flockTrailer is an array: [{ v1: [...] }, { v2: [...] }]
	const pdiVersionObj = PDIobj?.flockTrailer?.find(obj => obj[version]) || PDIobj?.flockTrailer?.[0];
	const groups = pdiVersionObj?.[version] || pdiVersionObj?.v1 || [];
	groups.forEach((group) => {
		group.sections?.forEach((section) => {
			section.items?.forEach((item) => {
				if (item?.form && Array.isArray(item.form)) {
					item.form.forEach((q) => {
						questions.push({
							label: q?.label || q?.id || "",
							id: q?.id,
							description: q?.description,
							type: q?.type,
						});
					});
				}
			});
		});
	});
	return questions;
};

// Collect all image fields from PDI structure.
// Scans the requested version first, then falls back to all defined versions
// so units saved under a different version still surface their photos.
const collectAllImageFields = (PDIobj, version = 'v1') => {
	const seen = new Set();
	const imageFields = [];

	const collectFromGroups = (groups) => {
		groups?.forEach((group) => {
			group?.sections?.forEach((section) => {
				section?.items?.forEach((item) => {
					if (item?.form && Array.isArray(item.form)) {
						item.form.forEach((q) => {
							if (q?.overrideComponent === "image" && q?.id && !seen.has(q.id)) {
								seen.add(q.id);
								imageFields.push({
									label: q?.label || q?.id || "",
									id: q?.id,
									description: q?.description,
								});
							}
						});
					}
				});
			});
		});
	};

	const versionObjects = PDIobj?.flockTrailer || [];
	const preferred = versionObjects.find((obj) => obj?.[version]);
	if (preferred?.[version]) collectFromGroups(preferred[version]);

	versionObjects.forEach((obj) => {
		Object.keys(obj || {}).forEach((key) => {
			if (key === version) return;
			collectFromGroups(obj[key]);
		});
	});

	return imageFields;
};

// Password masking
const maskIfPassword = (id, val, type) => {
	if (val === "Not Completed") return val;
	const isPw = (type && String(type).toLowerCase() === "password") || /password/i.test(id);
	if (!isPw) return val;
	const len = String(val).length;
	return "•".repeat(Math.max(len, 3));
};

// Quote specific id
const quoteIfUnitName = (id, val) =>
	val === "Not Completed" ? val : id === "unit_identifiers_unit_identifier_name" ? `"${val}"` : val;

// Monospace candidates
const urlRe = /^(https?:\/\/|www\.)/i;
const macRe = /^([0-9A-Fa-f]{2}[:\-]){5}([0-9A-Fa-f]{2})$/;
const ipRe = /^(?:(?:25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(?:\.|$)){4}/;
const serialHintRe = /(serial|imei|vin|mac|ip|url)/i;
const longAlphaNumRe = /^[A-Za-z0-9\-]{8,}$/;

const shouldMonospace = (id, label, val, type) => {
	if (!val || val === "Not Completed") return false;
	if (type && String(type).toLowerCase() === "url") return true;
	const s = String(val).trim();
	if (urlRe.test(s)) return true;
	if (macRe.test(s)) return true;
	if (ipRe.test(s)) return true;
	if (serialHintRe.test(id || "") || serialHintRe.test(label || "")) return true;
	if (longAlphaNumRe.test(s)) return true;
	return false;
};

// Image resolver
const resolveItemImageSrc = (item, imageBaseUrl) => {
	if (!item?.images || !item.images.length) return undefined;
	const first = String(item.images[0]);
	if (/^https?:\/\//i.test(first)) return first;
	if (imageBaseUrl) return `${imageBaseUrl}${first}`;
	return first;
};

const FinalSignoff = ({ user, date }) => {
	const hasValues = Boolean(user) || Boolean(date);
	return (
		<View style={styles.signoffWrap}>
			<Text style={styles.sectionHeader}>Final sign off</Text>
			<View style={styles.signoffBox}>
				{!hasValues ? (
					<>
						<View style={styles.signoffRow}>
							<Text style={styles.signoffLabel}>Signature</Text>
							<View style={styles.signoffLine} />
						</View>
						<View style={styles.signoffRow}>
							<Text style={styles.signoffLabel}>Date</Text>
							<View style={styles.signoffLine} />
						</View>
					</>
				) : (
					<>
						<View style={styles.signoffRow}>
							<Text style={styles.signoffLabel}>Signed by</Text>
							<Text style={styles.signoffValue}>{user || "—"}</Text>
						</View>
						<View style={styles.signoffRow}>
							<Text style={styles.signoffLabel}>Date</Text>
							<Text style={styles.signoffValue}>{date || "—"}</Text>
						</View>
					</>
				)}
			</View>
		</View>
	);
};

// Images gallery section component
// imageDataUrls is a map of field.id -> base64 data URL (pre-fetched)
const ImagesSection = ({ imageFields, imageDataUrls = {} }) => {
	// Filter to only images that have pre-fetched data URLs
	const imagesWithData = imageFields.filter((field) => {
		const dataUrl = imageDataUrls[field.id];
		return dataUrl && typeof dataUrl === "string" && dataUrl.trim() !== "";
	});

	// If no images, return null (don't render anything)
	if (imagesWithData.length === 0) return null;

	return (
		<View style={styles.imagesSection}>
			<Text style={styles.sectionHeader}>Unit Photos</Text>
			<View style={styles.imagesGrid}>
				{imagesWithData.map((field, idx) => {
					const imageUrl = imageDataUrls[field.id];
					return (
						<View key={`img-${idx}-${field.id}`} style={styles.imageCard}>
							{imageUrl && <Image src={imageUrl} style={styles.imageCardImage} />}
							<Text style={styles.imageCardLabel}>{field.label}</Text>
						</View>
					);
				})}
			</View>
		</View>
	);
};

// ----------------------- Row Components -----------------------
const HeaderRow = ({ showImage }) => (
	<View style={styles.headerRow}>
		{showImage ? <Text style={[styles.headerCell, { width: 70, textAlign: "center" }]}>Image</Text> : null}
		<Text style={[styles.headerCell1, { flex: 42 }]}>Item</Text>
		<Text style={[styles.headerCell1, { flex: 28 }]}>Result</Text>
		<Text style={[styles.headerCell, { flex: 10 }]}>Date</Text>
		<Text style={[styles.headerCell, { flex: 14 }]}>By</Text>
	</View>
);

// Pass/Fail + Not Completed styling in the value column
const KeyValueRow = ({
	label,
	description,
	base, // "Yes", "No", "Not Completed", or any string
	details,
	completedOn,
	completedBy,
	mono,
	imageSrc,
	showImage,
}) => {
	const isYes = base === "Yes";
	const isNo = base === "No";
	const isNC = base === "Not Completed";
	const display = isYes ? "Passed" : isNo ? "Failed" : base;

	const valueTextStyles = [styles.value];
	if (!isYes && !isNo && mono) valueTextStyles.push(styles.mono);
	if (isYes) valueTextStyles.push(styles.passText);
	if (isNo) valueTextStyles.push(styles.failText);
	if (isNC) valueTextStyles.push(styles.ncText);

	const smallTextStyles = [styles.valueSmall];
	if (isNo) smallTextStyles.push(styles.failSmall);
	if (isNC) smallTextStyles.push(styles.ncSmall);

	return (
		<View style={styles.row}>
			{showImage ? (
				<View style={styles.colImg}>
					{imageSrc && imageSrc.length && imageSrc?.map((src, index) => {
						return <Image key={`img-${index}`} src={ `https://rvmp.pages.dev/images/${src}` } style={styles.imgThumb} />;
					})}

					{/* {imageSrc ? <Image src={imageSrc} style={styles.imgThumb} /> : null} */}
				</View>
			) : null}

			<View style={styles.colLabel}>
				<Text style={styles.label}>{decodeEntities(label)}</Text>
				{description ? <Text style={styles.description}>{joinDescription(description)}</Text> : null}
			</View>

			<View style={[styles.colValue, isNo ? styles.failCell : null, isNC ? styles.ncCell : null]}>
				<Text style={valueTextStyles}>{display}</Text>
				{details ? <Text style={smallTextStyles}>Details: {details}</Text> : null}
			</View>

			<View style={styles.colOn}>
				<Text style={styles.metaText}>{completedOn || "—"}</Text>
			</View>
			<View style={styles.colBy}>
				<Text style={styles.metaText}>{completedBy || "—"}</Text>
			</View>
		</View>
	);
};



// ----------------------- Main PDF -----------------------
export const PDIReportPdf = ({
	formValues,
	title = "FLEX SECURITY TRAILER REPORT",
	metadata = {},
	imageBaseUrl,
	version = 'v1',
	imageDataUrls = {},
}) => {
	// PDI.flockTrailer is an array: [{ v1: [...] }, { v2: [...] }]
	const pdiVersionObj = PDI?.flockTrailer?.find(obj => obj[version]) || PDI?.flockTrailer?.[0];
	const allGroups = pdiVersionObj?.[version] || pdiVersionObj?.v1 || [];
	const questions = collectAllFormQuestions(PDI, version);
	const imageFields = collectAllImageFields(PDI, version);

	const subtitleUnit =
		formValues?.VIN
			? `"${String(formValues.VIN)}"`
			: metadata?.unitId
				? String(metadata.UUID)
				: "";

	const signUser = formValues?.final_signoff_user ? String(formValues.final_signoff_user) : undefined;
	const signDate = formatTimestamp(formValues?.final_signoff_user_Timestamp);

	return (
		<Document title={title} author="RVMP / Flex Security" subject="FLEX SECURITY TRAILER REPORT">
			{/* Cover / Unit Details page */}
			<Page size="A4" style={styles.page}>
				{/* Page 1 logo & company info */}
				<View style={styles.logoContainer}>
					<FlexSecurityLogo />
					<Text style={styles.companyInfo}>RVMP Flex Security&reg; | 54722 Chelsea, Elkhart, IN 46516</Text>
				</View>

				<Text style={styles.title}>{title}</Text>
				{subtitleUnit ? <Text style={styles.subtitle}>VIN: {subtitleUnit}</Text> : null}

				{questions.length > 0 && (
					<View style={{ marginTop: 6 }}>
						<Text style={styles.formHeader}>Unit Details</Text>
						<View style={styles.table}>
							<HeaderRow showImage={false} />
							{questions.map((q, idx) => {
								const meta = getMetaIfCompleted(q.id, formValues);
								const parts = getAnswerParts(q.id, formValues);
								let baseVal = parts.base;
								const detVal = parts.details;

								// mask & quote only for non-boolean textual values
								if (baseVal !== "Yes" && baseVal !== "No" && baseVal !== "Not Completed") {
									baseVal = maskIfPassword(q.id, baseVal, q.type);
									baseVal = quoteIfUnitName(q.id, baseVal);
								}

								const mono =
									parts.base && !["Yes", "No", "Not Completed"].includes(parts.base)
										? shouldMonospace(q.id, q.label, baseVal, q.type)
										: false;

								return (
									<KeyValueRow
										key={`form-${idx}-${q.id}`}
										label={q.label}
										description={q.description}
										base={baseVal}
										details={detVal}
										completedOn={meta.completedOn}
										completedBy={meta.completedBy}
										mono={mono}
										showImage={false}
									/>
								);
							})}
						</View>
					</View>
				)}

				<Footer />
			</Page>

			{/* One Page per Category; each page auto-paginates if overflow */}
			{allGroups.filter((g) => String(g?.category || "").trim().toLowerCase() !== "overview").map((group, gi) => (
				<Page key={`catpage-${gi}`} size="A4" style={styles.page}>
					<Text style={styles.sectionHeader}>{decodeEntities(group.category)}</Text>
					{group.description ? (
						<Text style={[styles.description, { marginTop: 4, marginBottom: 6 }]}>{joinDescription(group.description)}</Text>
					) : null}

					{group.sections?.map((section, si) => (
						<View key={`sec-${gi}-${si}`}>
							<View style={{ marginTop: 2, marginBottom: 2 }}>
								<Text style={styles.subHeader}>{decodeEntities(section.name)}</Text>
								{section.description ? <Text style={[styles.description, { marginTop: 2 }]}>{joinDescription(section.description)}</Text> : null}
							</View>

							<View style={styles.table}>
								<HeaderRow showImage={true} />
								{section.items?.filter((item) => !(item && Array.isArray(item.form) && item.form.length > 0)).map((item, ii) => {
									const meta = getMetaIfCompleted(item.slug, formValues);
									const parts = getAnswerParts(item.slug, formValues);
									const imgSrc = resolveItemImageSrc(item, imageBaseUrl);
									const mono =
										parts.base && !["Yes", "No", "Not Completed"].includes(parts.base)
											? shouldMonospace(item.slug, item.name, parts.base)
											: false;

									return (
										<KeyValueRow
											key={`item-${gi}-${si}-${ii}`}
											label={item.name}
											description={item.description}
											base={parts.base}
											details={parts.details}
											completedOn={meta.completedOn}
											completedBy={meta.completedBy}
											mono={mono}
											imageSrc={item.images || null}
											showImage={true}
										/>
									);
								})}
							</View>
						</View>
					))}

					<Footer />
				</Page>
			))}

			{/* Final sign off page with images */}
			<Page size="A4" style={styles.page}>
				<Text style={styles.title}>Final sign off</Text>
				<FinalSignoff user={signUser} date={signDate} />
				<ImagesSection imageFields={imageFields} imageDataUrls={imageDataUrls} />
				<Footer lastPage />
			</Page>
		</Document>
	);
};

// download link for PDF
const PDFDownload = ({ formValues, imageDataUrls = {}, imagesLoading = false }) => {
	const [fileName, setFileName] = useState(null);
	const getDateTime = () => {
		const now = new Date();
		const mm = String(now.getMonth() + 1).padStart(2, "0");
		const dd = String(now.getDate()).padStart(2, "0");
		const yyyy = now.getFullYear();
		const hh = String(now.getHours()).padStart(2, "0");
		const min = String(now.getMinutes()).padStart(2, "0");
		return `${mm}${dd}${yyyy}_${hh}${min}`;
	}
	/**
	 * Convert ISO date string to "YYYY-MM-DD_HH-MM" format.
	 * Example: "2025-10-05T20:26:00.129Z" -> "2025-10-05_20-26"
	 */
	const formatDateForFilename = (isoString) => {
		if (!isoString) return "";

		const date = new Date(isoString);

		const pad = (num) => String(num).padStart(2, "0");

		const year = date.getFullYear();
		const month = pad(date.getMonth() + 1); // months are 0-indexed
		const day = pad(date.getDate());
		const hours = pad(date.getHours());
		const minutes = pad(date.getMinutes());

		return `${year}-${month}-${day}_${hours}-${minutes}`;
	};

	useLayoutEffect(() => {
		if (formValues) {
			setFileName(`RVMP-Flex-Security_${formValues.unit_identifiers_unit_model ? formValues.unit_identifiers_unit_model.replaceAll(" ", "-").toUpperCase() : ""}_${formValues.Company ? formValues.Company.replaceAll(" ", "-") : ""}_${formValues.unit_identifiers_unit_identifier_name ? formValues.unit_identifiers_unit_identifier_name.replaceAll(" ", "-").toUpperCase() : ""}_${formValues["Updated On"] ? formatDateForFilename(formValues["Updated On"]) : ""}.pdf`);
			// update getDateTime to be new Date with MMDDYYYY_HHMM format
		}
	}, [formValues]);
	if (!formValues || !formValues.unit_identifiers_unit_identifier_name) {
		return <>Loading....</>;
	}
	if (imagesLoading) {
		return <div style={{ paddingTop: 20, paddingBottom: 20, textAlign: "center", width: "100%" }}>Loading images…</div>;
	}
	return (
		<PDFDownloadLink
			style={{ paddingTop: 20, paddingBottom: 20, display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", width: "100%" }}
			document={<PDIReportPdf formValues={formValues} title={`"${(formValues.unit_identifiers_unit_identifier_name || "").toUpperCase()}" REPORT`} metadata={{ unitId: formValues.unit_identifiers_unit_identifier_name }} imageBaseUrl="/assets/pdi/" imageDataUrls={imageDataUrls} />}
			fileName={fileName?.replaceAll(" ", "_").replaceAll(",", "_")}
		>
			{({ loading }) => (loading ? 'Loading...' : 'Download Document')}
		</PDFDownloadLink>
	);
};

// ----------------------- Screen Component -----------------------
export default function QCPDI() {
	const { currentUser } = useAuth();
	const [initialLoadValues, setInitialLoadValues] = useState({});
	const [formValues, setFormValues] = useState({});
	const [itemsChanged, setItemsChanged] = useState(false);
	const { addRowToSheet, loadSheetAsJson, getObjectByUUID, updateRowInSheet, saveSuccess } = useAppContext();
	const [isEditMode, setIsEditMode] = useState(false);
	const [unitId, setUnitId] = useState(null);
	const [rowData, setRowData] = useState(null);
	const [unitObject, setUnitObject] = useState(null);
	const [imageDataUrls, setImageDataUrls] = useState({});
	const [imagesLoading, setImagesLoading] = useState(false);

	const { categoryNavOpen, setCategoryNavOpen } = useCategoryNav();
	const { gcloudGetImage } = useGoogleStorageImage();
	const params = useParams();
	const paramId = params.id;
	const paramVersion = params.version || 'v1'; // Default to v1 if not specified

	useLayoutEffect(() => {
		if (unitId) {
			loadSheetAsJson().then((rows) => {
				const object = getObjectByUUID(rows, unitId);
				setInitialLoadValues(object || {});
				setFormValues(object || {});
				setUnitObject(object);
			});
		}
	}, [unitId]);

	useLayoutEffect(() => {
		if (paramId) {
			setIsEditMode(true);
			setUnitId(paramId);
		} else {
			console.log("No unit ID provided in URL");
		}
	}, [PDI && paramId]);

	// Fetch images when formValues changes
	useEffect(() => {
		let ignore = false;

		const fetchImages = async () => {
			if (!formValues || !currentUser) return;

			const imageFields = collectAllImageFields(PDI, paramVersion);
			const fieldsWithValues = imageFields.filter((field) => {
				const value = formValues[field.id];
				return value && typeof value === "string" && value.trim() !== "" && !value.startsWith("blob:");
			});

			console.log("[PDF] image fields with values:", fieldsWithValues.map(f => ({ id: f.id, filename: formValues[f.id] })));

			if (fieldsWithValues.length === 0) {
				if (!ignore) setImageDataUrls({});
				return;
			}

			if (!ignore) setImagesLoading(true);
			const urls = {};

			for (const field of fieldsWithValues) {
				if (ignore) return;
				const filename = formValues[field.id];
				try {
					// Skip if already a data URL or blob URL
					if (filename.startsWith("data:") || filename.startsWith("blob:")) {
						urls[field.id] = filename;
						continue;
					}
					const result = await gcloudGetImage(filename, false);
					if (result?.blob) {
						const dataUrl = await new Promise((resolve, reject) => {
							const reader = new FileReader();
							reader.onloadend = () => resolve(reader.result);
							reader.onerror = () => reject(reader.error);
							reader.readAsDataURL(result.blob);
						});
						urls[field.id] = dataUrl;
						console.log(`[PDF] loaded image ${field.id} (${result.blob.type}, ${result.blob.size} bytes)`);
					} else {
						console.warn(`[PDF] no blob returned for ${field.id} (${filename})`);
					}
				} catch (err) {
					console.error(`[PDF] failed to fetch image for ${field.id} (${filename}):`, err);
				}
			}

			if (!ignore) {
				setImageDataUrls(urls);
				setImagesLoading(false);
			}
		};

		fetchImages();

		return () => {
			ignore = true;
		};
	}, [formValues, currentUser, paramVersion]);

	const unitLabel =
		formValues?.unit_identifiers_unit_identifier_name
			? `"${String(formValues.unit_identifiers_unit_identifier_name)}"`
			: formValues?.UUID || "";

	return unitObject && formValues ? <>
		<PDFDownload formValues={formValues} imageDataUrls={imageDataUrls} imagesLoading={imagesLoading} />
		<PDFViewer>
			<PDIReportPdf
				formValues={formValues}
				title={`"${(formValues.unit_identifiers_unit_identifier_name || "").toUpperCase()}" REPORT`}
				metadata={{ unitId: unitLabel }}
				imageBaseUrl="/assets/pdi/"
				version={paramVersion}
				imageDataUrls={imageDataUrls}
			/>
		</PDFViewer>
	</> : (
		<>Loading…</>
	);
}
